import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useHelpAndRfq } from "../buyerStateDefs";
import { sendAuthenticatedRequest } from "../common/functions";
import useFirebaseAuthentication from "../common/hooks/useFirebaseAuthentication";
import { storePromotionCode } from "../common/promotionCodes";
import { recordVisit } from "../visitorTracking";
import { HelpAssistantModal } from "./InventorySearch/HelpAndRfq";
import { ExitIntentAuthModal } from "./ExitIntent/ExitIntentAuthModal";
import {
  defaultPromoView,
  type ExitIntentPromoView,
} from "./ExitIntent/exitIntentCopy";
import {
  hasScrolledFifthProduct,
  hasShownExitIntentToday,
  isBounceLeave,
  isFirstTimeVisit,
  LISTING_FIFTH_PRODUCT_EVENT,
  markExitIntentShownToday,
  rememberedAccountEmail,
} from "./ExitIntent/exitIntentTriggers";

const EXIT_INTENT_NOTICE =
  "Leaving so soon? Tell us what you're looking for and a Kono specialist will personally help you — we'll get back to you in less than 8 business hours.";

const OFFER_STORAGE_KEY = "signupPromoOfferId";
const PENDING_STORAGE_KEY = "signupPromoPending";

const DEFAULT_MAX_VISITS = 3;

type ExitIntentMode = "help" | "off" | "signup-promo";

const promoFromSiteConfig = (data: any): ExitIntentPromoView => {
  const fallback = defaultPromoView();
  return {
    kind:
      data?.promoKind === "value" ||
      data?.promoKind === "percent" ||
      data?.promoKind === "free-shipping"
        ? data.promoKind
        : fallback.kind,
    amount: Number(data?.amount) || fallback.amount,
    validDays: Number(data?.validDays) || fallback.validDays,
    freeShippingOrders:
      Number(data?.freeShippingOrders) || fallback.freeShippingOrders,
    freeShippingScope:
      data?.freeShippingScope === "duration" ? "duration" : "orders",
    offerText: String(data?.offerText || fallback.offerText),
  };
};

export function GlobalHelpAndRfq() {
  const rfqOpen = useHelpAndRfq((s) => s.rfqOpen);
  const helpOpen = useHelpAndRfq((s) => s.helpOpen);
  const helpNotice = useHelpAndRfq((s) => s.helpNotice);
  const helpStartWithHuman = useHelpAndRfq((s) => s.helpStartWithHuman);
  const helpInitialQuestion = useHelpAndRfq((s) => s.helpInitialQuestion);
  const closeHelp = useHelpAndRfq((s) => s.closeHelp);
  const closeRfq = useHelpAndRfq((s) => s.closeRfq);
  const openHelp = useHelpAndRfq((s) => s.openHelp);

  const { authUser, authIsLoading } = useFirebaseAuthentication();
  const [maxVisits, setMaxVisits] = useState<number | null>(null);
  const [mode, setMode] = useState<ExitIntentMode | null>(null);
  const [promo, setPromo] = useState<ExitIntentPromoView>(defaultPromoView());
  const [hasOfferImage, setHasOfferImage] = useState(false);
  const [imageSlot, setImageSlot] = useState<"discount" | "free-shipping">(
    "free-shipping",
  );
  const [promoOpen, setPromoOpen] = useState(false);
  const [offerId, setOfferId] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!rfqOpen) return;
    closeRfq();
    navigate("/rfq");
  }, [rfqOpen, closeRfq, navigate]);

  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BF_API_BASE_URL}/site-config`,
        );
        const data = await response.json();
        const configured = Number(data?.exitIntent?.maxVisits);
        const nextMode = data?.exitIntent?.mode as ExitIntentMode | undefined;
        if (!live) return;
        if (Number.isFinite(configured)) setMaxVisits(configured);
        else setMaxVisits(DEFAULT_MAX_VISITS);
        setMode(
          nextMode === "off" ||
            nextMode === "signup-promo" ||
            nextMode === "help"
            ? nextMode
            : "help",
        );
        setPromo(promoFromSiteConfig(data?.exitIntent));
        setHasOfferImage(Boolean(data?.exitIntent?.hasOfferImage));
        setImageSlot(
          data?.exitIntent?.imageSlot === "free-shipping"
            ? "free-shipping"
            : "discount",
        );
      } catch {
        if (live) {
          setMaxVisits(DEFAULT_MAX_VISITS);
          setMode("help");
        }
      }
    })();
    return () => {
      live = false;
    };
  }, []);

  useEffect(() => {
    if (authIsLoading || maxVisits === null || mode === null) return;
    if (authUser) return;
    if (location.pathname.match(/\/(sign-?in|sign-?up|forgot-password)/)) return;
    if (mode === "off" || maxVisits <= 0) return;
    if (hasShownExitIntentToday()) return;

    const visits = recordVisit();
    if (visits > maxVisits) return;

    let fired = false;

    const showWindow = () => {
      if (fired) return;
      if (hasShownExitIntentToday()) return;
      const { helpOpen: h, rfqOpen: r } = useHelpAndRfq.getState();
      if (h || r || promoOpen) return;
      fired = true;
      markExitIntentShownToday();
      if (mode === "signup-promo") {
        sessionStorage.setItem(PENDING_STORAGE_KEY, "1");
        void (async () => {
          try {
            const response = await fetch(
              `${process.env.REACT_APP_BF_API_BASE_URL}/promotions/offer`,
              { method: "POST" },
            );
            const data = await response.json().catch(() => ({}));
            if (data?.offerId) {
              sessionStorage.setItem(OFFER_STORAGE_KEY, String(data.offerId));
              setOfferId(String(data.offerId));
            }
            if (data?.offerText) {
              setPromo((current) => ({
                ...current,
                offerText: String(data.offerText),
              }));
            }
          } catch {
            /* still show the window */
          }
          setPromoOpen(true);
        })();
        return;
      }
      openHelp({ notice: EXIT_INTENT_NOTICE, startWithHuman: true });
    };

    const handleMouseOut = (e: MouseEvent) => {
      if (!isBounceLeave(e)) return;
      showWindow();
    };

    const handleListingScroll = () => {
      if (!isFirstTimeVisit(visits)) return;
      showWindow();
    };

    const armTimer = window.setTimeout(() => {
      document.addEventListener("mouseout", handleMouseOut);
      window.addEventListener(LISTING_FIFTH_PRODUCT_EVENT, handleListingScroll);
      if (hasScrolledFifthProduct()) handleListingScroll();
    }, 1500);

    return () => {
      window.clearTimeout(armTimer);
      document.removeEventListener("mouseout", handleMouseOut);
      window.removeEventListener(
        LISTING_FIFTH_PRODUCT_EVENT,
        handleListingScroll,
      );
    };
  }, [openHelp, authUser, authIsLoading, maxVisits, mode, promoOpen, location.pathname]);

  useEffect(() => {
    if (authIsLoading || !authUser) return;
    const pending = sessionStorage.getItem(PENDING_STORAGE_KEY);
    const storedOfferId = sessionStorage.getItem(OFFER_STORAGE_KEY);
    if (!pending && !storedOfferId) return;
    if (promoOpen) return;
    sessionStorage.removeItem(PENDING_STORAGE_KEY);
    sessionStorage.removeItem(OFFER_STORAGE_KEY);
    void (async () => {
      try {
        const response = await sendAuthenticatedRequest(
          `${process.env.REACT_APP_BF_API_BASE_URL}/promotions/claim`,
          { method: "POST", jsonBody: { offerId: storedOfferId } },
        );
        if (!response.ok) return;
        const data = await response.json();
        if (data?.code) {
          storePromotionCode({
            promotionCode: String(data.code),
            confirmationMessage: String(data.offerText || promo.offerText),
          });
        }
      } catch {
        /* claiming is best-effort */
      }
    })();
  }, [authUser, authIsLoading, promo.offerText, promoOpen]);

  const offerImageUrl = hasOfferImage
    ? `${process.env.REACT_APP_BF_API_BASE_URL}/promotions/offer-image?slot=${imageSlot}`
    : "";

  return (
    <>
      <HelpAssistantModal
        open={helpOpen}
        onClose={closeHelp}
        notice={helpNotice}
        startWithHuman={helpStartWithHuman}
        initialQuestion={helpInitialQuestion}
      />

      <ExitIntentAuthModal
        open={promoOpen}
        onClose={() => setPromoOpen(false)}
        promo={promo}
        offerImageUrl={offerImageUrl}
        offerId={offerId}
        initialEmail={rememberedAccountEmail()}
        startOnSignIn={Boolean(rememberedAccountEmail())}
      />
    </>
  );
}
